import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Clock, 
  Check, 
  X, 
  Heart,
  Calendar,
  MessageCircle,
  User,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

interface MyAdoptionRequest {
  id: string;
  adoption_post_id: string;
  message: string;
  meeting_availability: string | null;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
  adoption_post: {
    title: string;
    pet_name: string;
    image_url: string | null;
    user_id: string;
    location: string;
  };
  owner_profile: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
  reply_message?: string;
}

export default function MyAdoptionRequests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<MyAdoptionRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchMyRequests();
    }
  }, [user]);

  const fetchMyRequests = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      const { data: requestsData, error } = await supabase
        .from('adoption_requests')
        .select(`
          id,
          adoption_post_id,
          message,
          meeting_availability,
          status,
          reply_message,
          created_at,
          updated_at
        `)
        .eq('requester_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!requestsData || requestsData.length === 0) {
        setRequests([]);
        return;
      }

      // Get adoption posts info
      const { data: postsData, error: postsError } = await supabase
        .from('adoption_posts')
        .select('id, title, pet_name, image_url, user_id, location')
        .in('id', requestsData.map(r => r.adoption_post_id));

      if (postsError) throw postsError;

      // Get owner profiles
      const ownerIds = postsData?.map(p => p.user_id) || [];
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, username, display_name, avatar_url')
        .in('user_id', ownerIds);

      if (profilesError) throw profilesError;

      // Transform the data
      const transformedRequests = requestsData.map(request => {
        const post = postsData?.find(p => p.id === request.adoption_post_id);
        const profile = profilesData?.find(p => p.user_id === post?.user_id);

        return {
          ...request,
          status: request.status as 'pending' | 'approved' | 'rejected',
          adoption_post: post || {
            title: 'Post non trovato',
            pet_name: 'Sconosciuto',
            image_url: null,
            user_id: '',
            location: 'Sconosciuta'
          },
          owner_profile: profile || {
            username: 'utente_sconosciuto',
            display_name: null,
            avatar_url: null
          }
        } as MyAdoptionRequest;
      });

      setRequests(transformedRequests);
    } catch (error) {
      console.error('Error fetching my requests:', error);
      toast.error('Errore nel caricamento delle richieste');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      pending: { variant: 'secondary' as const, label: 'In attesa', icon: Clock, color: 'text-yellow-600' },
      approved: { variant: 'default' as const, label: 'Approvata', icon: Check, color: 'text-green-600' },
      rejected: { variant: 'destructive' as const, label: 'Rifiutata', icon: X, color: 'text-red-600' }
    };

    const config = variants[status as keyof typeof variants];
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className={`flex items-center gap-1 ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd MMMM yyyy, HH:mm', { locale: it });
  };

  const filteredRequests = {
    pending: requests.filter(r => r.status === 'pending'),
    approved: requests.filter(r => r.status === 'approved'),
    rejected: requests.filter(r => r.status === 'rejected'),
    all: requests
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/app/profile')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna al Profilo
          </Button>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Heart className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold text-white">Le Mie Richieste di Adozione</h1>
          </div>
          
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-muted rounded-lg" />
                    <div className="space-y-2 flex-1">
                      <div className="h-4 bg-muted rounded w-1/3" />
                      <div className="h-3 bg-muted rounded w-2/3" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/app/profile')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Torna al Profilo
        </Button>
      </div>
      
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Heart className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-white">Le Mie Richieste di Adozione</h1>
        </div>
        
        {requests.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">
                Nessuna richiesta inviata
              </h3>
              <p className="text-muted-foreground mb-4">
                Non hai ancora inviato richieste di adozione
              </p>
              <Button onClick={() => navigate('/app/adoption')}>
                Sfoglia Annunci
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="all" className="space-y-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="all" className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                Tutte ({filteredRequests.all.length})
              </TabsTrigger>
              <TabsTrigger value="pending" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                In Attesa ({filteredRequests.pending.length})
              </TabsTrigger>
              <TabsTrigger value="approved" className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                Approvate ({filteredRequests.approved.length})
              </TabsTrigger>
              <TabsTrigger value="rejected" className="flex items-center gap-2">
                <X className="w-4 h-4" />
                Rifiutate ({filteredRequests.rejected.length})
              </TabsTrigger>
            </TabsList>

            {Object.entries(filteredRequests).map(([status, statusRequests]) => (
              <TabsContent key={status} value={status} className="space-y-4">
                {statusRequests.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <p className="text-muted-foreground">
                        Nessuna richiesta {status === 'all' ? '' : 
                          status === 'pending' ? 'in attesa' : 
                          status === 'approved' ? 'approvata' : 'rifiutata'}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  statusRequests.map((request) => (
                    <Card key={request.id} className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex gap-4">
                          {/* Pet Image */}
                          <div className="flex-shrink-0">
                            <img
                              src={request.adoption_post.image_url || '/placeholder.svg'}
                              alt={request.adoption_post.pet_name}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 space-y-3">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-semibold text-white">
                                  {request.adoption_post.pet_name}
                                </h3>
                                <p className="text-sm text-muted-foreground">
                                  {request.adoption_post.title}
                                </p>
                              </div>
                              {getStatusBadge(request.status)}
                            </div>
                            
                            {/* Owner Info */}
                            <div className="flex items-center gap-2">
                              <Avatar className="w-6 h-6">
                                <AvatarImage src={request.owner_profile.avatar_url || undefined} />
                                <AvatarFallback className="text-xs">
                                  {request.owner_profile.username.charAt(0).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-sm text-muted-foreground">
                                Proprietario: {request.owner_profile.display_name || request.owner_profile.username}
                              </span>
                            </div>
                            
                            {/* My Request Message */}
                            <div className="bg-muted/10 p-3 rounded">
                              <p className="text-sm text-muted-foreground mb-1 font-medium">
                                Il tuo messaggio:
                              </p>
                              <p className="text-sm">{request.message}</p>
                              {request.meeting_availability && (
                                <p className="text-xs text-muted-foreground mt-2">
                                  <strong>Disponibilità:</strong> {request.meeting_availability}
                                </p>
                              )}
                            </div>
                            
                            {/* Reply from Owner */}
                            {request.reply_message && (
                              <div className={`p-3 rounded border-l-4 ${
                                request.status === 'approved' 
                                  ? 'bg-green-50/10 border-green-500' 
                                  : 'bg-red-50/10 border-red-500'
                              }`}>
                                <p className="text-sm text-muted-foreground mb-1 font-medium">
                                  Risposta del proprietario:
                                </p>
                                <p className="text-sm">{request.reply_message}</p>
                              </div>
                            )}
                            
                            {/* Dates */}
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Inviata: {formatDate(request.created_at)}
                              </span>
                              {request.updated_at !== request.created_at && (
                                <span>
                                  Aggiornata: {formatDate(request.updated_at)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>
    </div>
  );
}